import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { LogOut, Menu, X } from "lucide-react";
import { api } from "@/lib/api";
import { Language, Logo } from "@/components/Brand";
import StrategyRoomCTA from "@/components/workspace/StrategyRoomCTA";
import { pathForSection, sectionFromPathname, WORKSPACE_BASE, WORKSPACE_SECTIONS } from "@/workspace/sections";

export function WorkspaceIndex() {
  const [params] = useSearchParams();
  if (params.get("payment")) {
    return <Navigate to={`${WORKSPACE_BASE}/growth-club?${params.toString()}`} replace />;
  }
  return <Navigate to={`${WORKSPACE_BASE}/home`} replace />;
}

export function WorkspaceLegacyRedirect({ to }) {
  const [params] = useSearchParams();
  const query = params.toString();
  return <Navigate to={`${WORKSPACE_BASE}/${to}${query ? `?${query}` : ""}`} replace />;
}

export function WorkspaceScreen({ Screen }) {
  const ctx = useOutletContext();
  return <Screen {...ctx} />;
}

export default function Workspace({ lang, t, change, setUser }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const [checkout, setCheckout] = useState(params.get("payment") ? { payment: params.get("payment"), provider: params.get("provider"), sessionId: params.get("session_id"), token: params.get("token") } : null);
  const section = sectionFromPathname(location.pathname);

  useEffect(() => {
    api.get("/workspace").then((r) => { setData(r.data); setUser(r.data.user); }).catch(() => navigate("/login", { replace: true }));
  }, [navigate, setUser]);

  useEffect(() => { setNavOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (event) => { if (event.key === "Escape") setNavOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  const logout = async () => { await api.post("/auth/logout", {}); setUser(null); navigate("/"); };
  const setSection = (key) => navigate(pathForSection(key));

  if (!data) return <div className="loading" data-testid="workspace-loading">{t.loading}</div>;

  const user = data.user;
  const hour = new Date().getHours();
  const greeting = lang === "fa" ? (hour < 12 ? "صبح بخیر" : hour < 18 ? "ظهر بخیر" : "عصر بخیر") : (hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening");

  return (
    <div className="workspace" data-testid="workspace-page">
      {navOpen && <button type="button" className="sidebar-backdrop" aria-label={t.closeMenu} onClick={() => setNavOpen(false)} />}
      <aside id="workspace-sidebar" className={`sidebar ${navOpen ? "open" : ""}`} data-testid="workspace-sidebar">
        <Logo t={t} />
        <div className="workspace-label">{t.workspace}</div>
        <nav>
          {WORKSPACE_SECTIONS.map(({ id, path, icon: Icon, labelKey }) => (
            <NavLink
              key={id}
              to={`${WORKSPACE_BASE}/${path}`}
              end
              className={({ isActive }) => (isActive ? "selected" : "")}
              data-testid={`workspace-nav-${id}`}
              aria-label={t[labelKey]}
              onClick={() => setNavOpen(false)}
            >
              <Icon size={17} />
              <span>{t[labelKey]}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <button type="button" onClick={() => change(lang === "en" ? "fa" : "en")} data-testid="workspace-language-button"><span>{lang === "en" ? "فارسی" : "English"}</span></button>
          <button type="button" onClick={logout} data-testid="workspace-logout-button"><LogOut size={16} /><span>{lang === "fa" ? "خروج" : "Log out"}</span></button>
        </div>
      </aside>
      <main className="workspace-main">
        <header className="workspace-header">
          <button type="button" className="nav-toggle" aria-expanded={navOpen} aria-controls="workspace-sidebar" onClick={() => setNavOpen((open) => !open)} data-testid="workspace-nav-toggle">
            {navOpen ? <X size={18} /> : <Menu size={18} />}
            <span className="sr-only">{navOpen ? t.closeMenu : t.openMenu}</span>
          </button>
          <div className="header-copy"><p className="eyebrow">{greeting}</p><h1>{t[section.labelKey]}</h1></div>
          <div className="header-tools">
            <div className="workspace-language-switcher" data-testid="workspace-language-switcher"><Language lang={lang} change={change} /></div>
            <StrategyRoomCTA t={t} testId="header-strategy-room" />
            <div className="profile-chip" data-testid="workspace-user-profile">{user?.picture ? <img src={user.picture} alt="" /> : <span>{(user?.name || "N")[0]}</span>}{user?.name}</div>
          </div>
        </header>
        <Outlet context={{ lang, t, data, setData, setSection, checkout, clearCheckout: () => setCheckout(null) }} />
      </main>
    </div>
  );
}
